import React, { Suspense, lazy, ComponentType } from 'react';
import { CircularProgress, Box } from '@mui/material';
import { SkeletonLoader } from '../common/SkeletonLoader';
import { measurePerformance } from '../../utils/performance';

interface LazyRouteProps {
  component: () => Promise<{ default: ComponentType<any> }>;
  fallback?: React.ReactNode;
  enablePerformanceTracking?: boolean;
  routeName?: string;
}

// Default fallback component with skeleton loader
const DefaultFallback: React.FC<{ routeName?: string }> = ({ routeName }) => (
  <Box sx={{ padding: 2 }}>
    <SkeletonLoader variant="card" count={3} />
    {routeName && (
      <Box sx={{ 
        textAlign: 'center', 
        marginTop: 2, 
        color: 'text.secondary',
        fontSize: '0.875rem'
      }}>
        Loading {routeName}...
      </Box>
    )}
  </Box>
);

// Simple spinner fallback
const SpinnerFallback: React.FC = () => (
  <Box 
    sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '50vh' 
    }}
  >
    <CircularProgress size={40} />
  </Box>
);

// Performance-aware lazy component wrapper
export const LazyRoute: React.FC<LazyRouteProps> = ({
  component,
  fallback,
  enablePerformanceTracking = true,
  routeName = 'Unknown Route'
}) => {
  // Create lazy component with performance tracking
  const LazyComponent = React.useMemo(() => {
    let lazyComp: ComponentType<any>;
    
    if (enablePerformanceTracking) {
      // Wrap the import with performance measurement
      const timedComponent = () => {
        const timer = measurePerformance.startTimer(`route-load-${routeName}`);
        return component().then(module => {
          const duration = timer.end();
          console.log(`📦 [PERF] Route "${routeName}" loaded in ${duration.toFixed(2)}ms`);
          return module;
        });
      };
      lazyComp = lazy(timedComponent);
    } else {
      lazyComp = lazy(component);
    }
    
    return lazyComp;
  }, [component, enablePerformanceTracking, routeName]);

  // Use provided fallback or default
  const fallbackComponent = fallback || <DefaultFallback routeName={routeName} />;

  return (
    <Suspense fallback={fallbackComponent}>
      <LazyComponent />
    </Suspense>
  );
};

// Pre-built lazy route components for common pages
export const LazyProjects = () => (
  <LazyRoute
    component={() => import('../../pages/Projects')}
    routeName="Projects"
  />
);

export const LazyDashboard = () => (
  <LazyRoute
    component={() => import('../../pages/Dashboard')}
    routeName="Dashboard"
  />
);

export const LazyVehicles = () => (
  <LazyRoute
    component={() => import('../../pages/Vehicles')}
    routeName="Vehicles"
  />
);

export const LazyLocations = () => (
  <LazyRoute
    component={() => import('../../pages/Locations')}
    routeName="Locations"
  />
);

export const LazyJobs = () => (
  <LazyRoute
    component={() => import('../../pages/Jobs')}
    routeName="Jobs"
  />
);

export const LazyOptimization = () => (
  <LazyRoute
    component={() => import('../../pages/Optimization')}
    routeName="Optimization"
  />
);

export const LazyOptimizationResults = () => (
  <LazyRoute
    component={() => import('../../pages/OptimizationResults')}
    routeName="Optimization Results"
  />
);

export const LazyMapView = () => (
  <LazyRoute
    component={() => import('../../pages/MapView')}
    routeName="Map View"
  />
);

export const LazyScheduleView = () => (
  <LazyRoute
    component={() => import('../../pages/ScheduleView')}
    routeName="Schedule View"
  />
);

export const LazyDataManagement = () => (
  <LazyRoute
    component={() => import('../../pages/DataManagement')}
    routeName="Data Management"
  />
);

export const LazyScenarioManagement = () => (
  <LazyRoute
    component={() => import('../../pages/ScenarioManagement')}
    routeName="Scenario Management"
  />
);

export const LazyTableEditor = () => (
  <LazyRoute
    component={() => import('../../pages/TableEditor')}
    routeName="Table Editor"
  />
);

export const LazySettings = () => (
  <LazyRoute
    component={() => import('../../pages/Settings')}
    routeName="Settings"
  />
);

// Utility for creating custom lazy routes
export const createLazyRoute = (
  importFn: () => Promise<{ default: ComponentType<any> }>,
  routeName: string,
  customFallback?: React.ReactNode
) => {
  return () => (
    <LazyRoute
      component={importFn}
      routeName={routeName}
      fallback={customFallback}
    />
  );
};

// Route preloading utilities
export const preloadRoute = (importFn: () => Promise<{ default: ComponentType<any> }>) => {
  return importFn();
};

// Preload critical routes on app start
export const preloadCriticalRoutes = () => {
  // Only preload the most critical routes to avoid overwhelming the network
  setTimeout(() => {
    preloadRoute(() => import('../../pages/Dashboard'));
    preloadRoute(() => import('../../pages/Projects'));
  }, 1000); // Delay to avoid blocking initial render
};

// Hook for route preloading on hover/focus
export const useRoutePreloader = () => {
  const preloadedRoutes = React.useRef(new Set<string>());

  const preloadOnHover = React.useCallback((routeName: string, importFn: () => Promise<any>) => {
    return {
      onMouseEnter: () => {
        if (!preloadedRoutes.current.has(routeName)) {
          preloadedRoutes.current.add(routeName);
          importFn().catch(() => {
            // Remove from cache if preload failed
            preloadedRoutes.current.delete(routeName);
          });
        }
      }
    };
  }, []);

  return { preloadOnHover };
};